import { FilteredListViewModel } from "@frui.ts/datascreens";
import { attachAutomaticDirtyWatcher } from "@frui.ts/dirtycheck";
import type { PropertyName } from "@frui.ts/helpers";
import { BusyWatcher, watchBusy } from "@frui.ts/screens";
import { action, computed, observable } from "mobx";
import type { EntityNames } from "config";
import { Modules } from "config";
import type EventBus from "services/eventBus";
import type LocalizationService from "services/localizationService";
import type SecurityService from "services/securityService";

export default abstract class CallCenterListViewModel<TEntity, TSearchRequest> extends FilteredListViewModel<
  TEntity,
  TSearchRequest
> {
  protected abstract readonly modelName: EntityNames;
  protected abstract localizationService: LocalizationService;
  protected abstract securityService: SecurityService;
  protected abstract eventBus: EventBus;
  protected unsubscriptions: Array<() => void> = [];
  protected abortController?: AbortController;

  @observable openItem?: TEntity;
  @observable busyWatcher = new BusyWatcher();
  @observable loading = false;

  constructor() {
    super();
    this.items = [];
  }

  protected abstract loadItems(): Promise<void>;
  protected abstract loadRelatedCounts(): Promise<void>;
  protected abstract publishTotalCount(totalCount: number): void;
  protected abstract resetRelatedCounts(): void;

  @action.bound
  @watchBusy
  public async loadData() {
    this.loading = true;
    this.abortController = new AbortController();

    try {
      await this.loadItems();
    } catch (error) {
      this.onLoadItemsFailed();
      return;
    } finally {
      this.finishLoading();
    }

    try {
      await this.loadRelatedCounts();
    } catch (error) {
      this.resetRelatedCounts();
    }
  }

  @action
  protected finishLoading() {
    this.loading = false;
    this.abortController = undefined;
  }

  @action
  protected onLoadItemsFailed() {
    this.resetItems();
    this.pagingFilter.offset = 0;
    this.publishTotalCount(0);
    this.resetRelatedCounts();
  }

  protected onDeactivate(isClosing: boolean): Promise<void> {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubsribe => unsubsribe());
      this.unsubscriptions = [];
    }

    return super.onDeactivate(isClosing);
  }

  protected async deactivateChild(child: FilteredListViewModel<any> | undefined, isClosing: boolean) {
    this.openItem = undefined;
    return super.deactivateChild(child, isClosing);
  }

  @computed
  get allowWrite() {
    return this.securityService.allowTo("callcenter", "write");
  }

  navigate(subPath: string | undefined, params: any) {
    return this.search(params);
  }

  async search(filter: TSearchRequest) {
    this.filter = attachAutomaticDirtyWatcher(filter, true);
    await this.applyFilterAndLoad();
    this.notifyNavigationChanged();
  }

  @action.bound
  resetItems() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = undefined;
    }
    this.items = [];
    this.currentPaging = {
      totalItems: 0,
      offset: 0,
      limit: 15,
    };
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.callCenter, code, size);
  }

  translateModel(model: EntityNames, size?: number) {
    return this.localizationService.translateModel(model, size);
  }

  translateAttribute(attribute: PropertyName<TEntity>, size?: number) {
    return this.localizationService.translateAttribute(this.modelName, attribute, size);
  }
}
